"use strict";
// A maintenance option's "Maintenance Agreement Set Up" (#515): what it stores in hig_scope.maintenance
// (option-scope.ts, v3), and the set-up pricing is fed (maintenance.ts). The agreement years and the tier
// are the option's own columns (hig_termyears, hig_maintenancetier); everything else is here. The two
// template constants are pricing settings, never code (decision 6, DD 2026-10-23). Pure.
Object.defineProperty(exports, "__esModule", { value: true });
exports.defaultStartYear = exports.MAINTENANCE_YEARS = exports.SERVICE_FRACTION_SETTING = exports.SECOND_REPAINT_COATS_SETTING = exports.maintenanceYearsOf = exports.MAINTENANCE_TIERS = exports.DEFAULT_MAINTENANCE_YEARS = exports.OPTION_KIND = void 0;
exports.setMaintenance = setMaintenance;
exports.setYearWork = setYearWork;
exports.setServiceExtra = setServiceExtra;
exports.programmesOf = programmesOf;
exports.maintenanceSetup = maintenanceSetup;
const maintenance_contract_ts_1 = require("./maintenance-contract.js");
const maintenance_payments_ts_1 = require("./maintenance-payments.js");
const pricing_inputs_ts_1 = require("./pricing-inputs.js");
/** The template's way of saying "not serviced": Extra Service −100 % (S = X × fraction × 0). */
const NO_SERVICING = -1;
/** hig_scopeoption.hig_kind's labels (the API's records.ts). */
exports.OPTION_KIND = { repaint: 'Repaint', maintenance: 'Maintenance contract' };
/** A new maintenance option's agreement years: the template's (Take Off Z7). */
exports.DEFAULT_MAINTENANCE_YEARS = 5;
/** hig_maintenancetier's labels, in CRM's order (opportunity.hig_maintenancetier). */
exports.MAINTENANCE_TIERS = ['Enhance', 'Maintain', 'Protect'];
/** A maintenance option's agreement years: its own column, else the default. */
const maintenanceYearsOf = (fields) => typeof fields.hig_termyears === 'number' ? fields.hig_termyears : exports.DEFAULT_MAINTENANCE_YEARS;
exports.maintenanceYearsOf = maintenanceYearsOf;
exports.SECOND_REPAINT_COATS_SETTING = 'maintenance.secondrepaintcoats';
exports.SERVICE_FRACTION_SETTING = 'maintenance.servicefraction';
/** CRM's year products run A … L (A01L … L01L), so a contract is 2–12 years, the repaint year included. */
exports.MAINTENANCE_YEARS = { min: 2, max: 12 };
const PERCENT = 100;
const DATE_YEAR_LENGTH = 4;
/** Change the set-up; a value set to null or undefined goes back to its default, and an empty set-up is dropped. */
function setMaintenance(scope, change) {
    const merged = Object.entries({ ...scope.maintenance, ...change }).filter(([, v]) => v !== null && v !== undefined && v !== false);
    const result = { ...scope };
    if (merged.length)
        result.maintenance = Object.fromEntries(merged);
    else
        delete result.maintenance;
    return result;
}
/**
 * Set one area's work for one contract year (1-based); null puts it back to the default. An area whose
 * years are all the default stores nothing, so changing the term later moves its defaults with it.
 */
function setYearWork(scope, areaId, year, work) {
    const programme = { ...scope.maintenance?.programme };
    const years = [...(programme[areaId] ?? [])];
    while (years.length < year)
        years.push(null);
    years[year - 1] = work;
    const defaults = (0, maintenance_contract_ts_1.defaultProgramme)(years.length);
    const kept = years.map((w, i) => (w === defaults[i] ? null : w));
    while (kept.length && kept[kept.length - 1] === null)
        kept.pop();
    if (kept.length)
        programme[areaId] = kept;
    else
        delete programme[areaId];
    return setMaintenance(scope, { programme: Object.keys(programme).length ? programme : null });
}
/** One surface's servicing for this option; null goes back to the set-up's Extra Servicing %. */
function setServiceExtra(scope, areaId, rowId, choice) {
    const all = { ...scope.maintenance?.serviceExtra };
    const area = { ...all[areaId] };
    if (choice === null)
        delete area[rowId];
    else
        area[rowId] = choice;
    if (Object.keys(area).length)
        all[areaId] = area;
    else
        delete all[areaId];
    return setMaintenance(scope, { serviceExtra: Object.keys(all).length ? all : null });
}
/** The stored per-surface choices as pricing's fractions (no servicing = −1). */
function rowExtrasOf(stored) {
    return Object.fromEntries(Object.entries(stored ?? {}).map(([area, rows]) => [
        area,
        Object.fromEntries(Object.entries(rows).map(([row, choice]) => [row, choice === 'none' ? NO_SERVICING : choice / PERCENT])),
    ]));
}
/** Each area's programme for a `years`-long contract, from what the option stores (pricing's input). */
function programmesOf(scope, areaIds, years) {
    const stored = scope.maintenance?.programme ?? {};
    return Object.fromEntries(areaIds.map((id) => [id, (0, maintenance_contract_ts_1.programmeFor)(stored[id] ?? undefined, years)]));
}
/** The setting `name` on the pricing date; a missing one is a CRM data problem, said plainly. */
function setting(reference, name) {
    const value = reference.settings[name];
    if (typeof value !== 'number')
        throw new pricing_inputs_ts_1.PricingDataError(`CRM is missing the pricing setting ${name} on ${reference.date} — maintenance can't be priced without it.`);
    return value;
}
/** The year a contract starts from by default: the pricing date's. */
const defaultStartYear = (reference) => Number(reference.date.slice(0, DATE_YEAR_LENGTH));
exports.defaultStartYear = defaultStartYear;
/** The set-up a maintenance option is priced with: its choices over the branch's rates and the settings on the pricing date. */
function maintenanceSetup(reference, option, years) {
    if (!Number.isInteger(years) || years < exports.MAINTENANCE_YEARS.min || years > exports.MAINTENANCE_YEARS.max) {
        throw new pricing_inputs_ts_1.PricingDataError(`A maintenance contract is ${exports.MAINTENANCE_YEARS.min} to ${exports.MAINTENANCE_YEARS.max} years, the repaint year included.`);
    }
    const choice = option.scope.maintenance ?? {};
    const servicing = (0, pricing_inputs_ts_1.labourRateFor)(reference, choice.servicingLabourType || option.labourType).labour ?? { sell: 0, cost: 0 };
    return {
        years,
        servicing,
        extraRate: { sell: choice.extraRate?.sell ?? 0, cost: choice.extraRate?.cost ?? 0 },
        secondRepaintCoats: choice.secondRepaintCoats ?? setting(reference, exports.SECOND_REPAINT_COATS_SETTING),
        serviceFraction: setting(reference, exports.SERVICE_FRACTION_SETTING),
        extraServicing: (choice.extraServicing ?? 0) / PERCENT,
        includeAccess: choice.includeAccess === true,
        includePrelims: choice.includePrelims === true,
        rowExtras: rowExtrasOf(choice.serviceExtra),
        // The finance rate is read only for a financed type, so a PAYG contract doesn't need the setting.
        payment: choice.paymentType ? { type: choice.paymentType, financeRate: setting(reference, maintenance_payments_ts_1.FINANCE_RATE_SETTING) / PERCENT } : { type: 'PAYG', financeRate: 0 },
    };
}
